import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getSelection, $isRangeSelection, CAN_REDO_COMMAND, CAN_UNDO_COMMAND, COMMAND_PRIORITY_CRITICAL, FORMAT_TEXT_COMMAND, REDO_COMMAND, SELECTION_CHANGE_COMMAND, UNDO_COMMAND } from 'lexical'
import { $setBlocksType } from '@lexical/selection'
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text'
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list'
import { TOGGLE_LINK_COMMAND } from '@lexical/link'
import { INSERT_TABLE_COMMAND } from '@lexical/table'

type ToolbarButtonProps = {
  title: string
  onClick: () => void
  disabled?: boolean
  active?: boolean
  children: React.ReactNode
}

function ToolbarButton({ title, onClick, disabled, active, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      className={`lexical-toolbar__btn ${active ? 'lexical-toolbar__btn--active' : ''}`}
      title={title}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  )
}

function Icon({ children }: { children: React.ReactNode }) {
  return (
    <svg className="lexical-toolbar__icon" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  )
}

type ToolbarItem =
  | { type: 'sep' }
  | { type: 'btn'; title: string; onClick: () => void; icon: React.ReactNode; disabled?: boolean; active?: boolean }

export const LexicalToolbarPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext()
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)
  const [isCode, setIsCode] = useState(false)

  const updateTextFormatState = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) return
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsUnderline(selection.hasFormat('underline'))
      setIsStrikethrough(selection.hasFormat('strikethrough'))
      setIsCode(selection.hasFormat('code'))
    })
  }, [editor])

  useEffect(() => {
    updateTextFormatState()
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateTextFormatState()
        return false
      },
      COMMAND_PRIORITY_CRITICAL
    )
  }, [editor, updateTextFormatState])

  useEffect(() => {
    const unregisterUndo = editor.registerCommand(
      CAN_UNDO_COMMAND,
      (payload) => {
        setCanUndo(payload)
        return false
      },
      COMMAND_PRIORITY_CRITICAL
    )
    const unregisterRedo = editor.registerCommand(
      CAN_REDO_COMMAND,
      (payload) => {
        setCanRedo(payload)
        return false
      },
      COMMAND_PRIORITY_CRITICAL
    )
    return () => {
      unregisterUndo()
      unregisterRedo()
    }
  }, [editor])

  const setHeading = useCallback((tag: 'h1' | 'h2' | 'h3') => {
    editor.update(() => {
      const selection = $getSelection()
      $setBlocksType(selection, () => $createHeadingNode(tag))
    })
  }, [editor])

  const setQuote = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection()
      $setBlocksType(selection, () => $createQuoteNode())
    })
  }, [editor])

  const insertTable = useCallback(() => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, { columns: '3', rows: '3', includeHeaders: { rows: true, columns: true } })
  }, [editor])

  const toggleLink = useCallback(() => {
    const url = window.prompt('Enter URL (leave empty to remove link):', 'https://')
    if (url === null) return
    const trimmed = url.trim()
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, trimmed ? trimmed : null)
  }, [editor])

  const buttons = useMemo(() => {
    const items: ToolbarItem[] = [
      { type: 'btn', title: 'Undo', onClick: () => { editor.dispatchCommand(UNDO_COMMAND, undefined) }, disabled: !canUndo, icon: (
        <Icon><path d="M3 7v6h6" /><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" /></Icon>
      ) },
      { type: 'btn', title: 'Redo', onClick: () => { editor.dispatchCommand(REDO_COMMAND, undefined) }, disabled: !canRedo, icon: (
        <Icon><path d="M21 7v6h-6" /><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7" /></Icon>
      ) },
      { type: 'sep' },
      { type: 'btn', title: 'Bold (Ctrl+B)', onClick: () => { editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold') }, active: isBold, icon: (
        <Icon><path d="M6 12h9a4 4 0 0 1 0 8H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h7a4 4 0 0 1 0 8" /></Icon>
      ) },
      { type: 'btn', title: 'Italic (Ctrl+I)', onClick: () => { editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic') }, active: isItalic, icon: (
        <Icon><line x1="19" x2="10" y1="4" y2="4" /><line x1="14" x2="5" y1="20" y2="20" /><line x1="15" x2="9" y1="4" y2="20" /></Icon>
      ) },
      { type: 'btn', title: 'Underline (Ctrl+U)', onClick: () => { editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline') }, active: isUnderline, icon: (
        <Icon><path d="M6 4v6a6 6 0 0 0 12 0V4" /><line x1="4" x2="20" y1="20" y2="20" /></Icon>
      ) },
      { type: 'btn', title: 'Strikethrough', onClick: () => { editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough') }, active: isStrikethrough, icon: (
        <Icon><path d="M16 4H9a3 3 0 0 0-2.83 4" /><path d="M14 12a4 4 0 0 1 0 8H6" /><line x1="4" x2="20" y1="12" y2="12" /></Icon>
      ) },
      { type: 'btn', title: 'Inline Code', onClick: () => { editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code') }, active: isCode, icon: (
        <Icon><path d="m16 18 6-6-6-6" /><path d="m8 6-6 6 6 6" /></Icon>
      ) },
      { type: 'sep' },
      { type: 'btn', title: 'Heading 1', onClick: () => setHeading('h1'), icon: (
        <Icon><path d="M4 12h8" /><path d="M4 18V6" /><path d="M12 18V6" /><path d="m17 12 3-2v8" /></Icon>
      ) },
      { type: 'btn', title: 'Heading 2', onClick: () => setHeading('h2'), icon: (
        <Icon><path d="M4 12h8" /><path d="M4 18V6" /><path d="M12 18V6" /><path d="M21 18h-4c0-4 4-3 4-6 0-1.5-2-2.5-4-1" /></Icon>
      ) },
      { type: 'btn', title: 'Heading 3', onClick: () => setHeading('h3'), icon: (
        <Icon><path d="M4 12h8" /><path d="M4 18V6" /><path d="M12 18V6" /><path d="M17.5 10.5c1.7-1 3.5 0 3.5 1.5a2 2 0 0 1-2 2" /><path d="M17 17.5c2 1.5 4 .3 4-1.5a2 2 0 0 0-2-2" /></Icon>
      ) },
      { type: 'sep' },
      { type: 'btn', title: 'Bullet List', onClick: () => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined), icon: (
        <Icon><path d="M3 5h.01" /><path d="M3 12h.01" /><path d="M3 19h.01" /><path d="M8 5h13" /><path d="M8 12h13" /><path d="M8 19h13" /></Icon>
      ) },
      { type: 'btn', title: 'Numbered List', onClick: () => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined), icon: (
        <Icon><path d="M11 5h10" /><path d="M11 12h10" /><path d="M11 19h10" /><path d="M4 4h1v5" /><path d="M4 9h2" /><path d="M6.5 20H3.4c0-1 2.6-1.925 2.6-3.5a1.5 1.5 0 0 0-2.6-1.02" /></Icon>
      ) },
      { type: 'btn', title: 'Quote', onClick: setQuote, icon: (
        <Icon><path d="M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z" /><path d="M5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z" /></Icon>
      ) },
      { type: 'sep' },
      { type: 'btn', title: 'Insert Link', onClick: toggleLink, icon: (
        <Icon><path d="M9 17H7A5 5 0 0 1 7 7h2" /><path d="M15 7h2a5 5 0 1 1 0 10h-2" /><line x1="8" x2="16" y1="12" y2="12" /></Icon>
      ) },
      { type: 'btn', title: 'Insert Table', onClick: insertTable, icon: (
        <Icon><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 10h18" /><path d="M3 14h18" /><path d="M8 5v14" /><path d="M16 5v14" /></Icon>
      ) },
    ]
    return items
  }, [canRedo, canUndo, editor, insertTable, isBold, isCode, isItalic, isStrikethrough, isUnderline, setHeading, setQuote, toggleLink])

  return (
    <div className="lexical-toolbar">
      {buttons.map((b, idx) => {
        if (b.type === 'sep') {
          return <div key={`sep-${idx}`} className="lexical-toolbar__sep" />
        }
        return (
          <ToolbarButton
            key={`${b.title}-${idx}`}
            title={b.title}
            onClick={b.onClick}
            disabled={b.disabled}
            active={'active' in b ? b.active : false}
          >
            {b.icon}
          </ToolbarButton>
        )
      })}
    </div>
  )
}

